import React, { useEffect, useState } from "react";
import { Ban } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import { selectSessionOccurrences } from "../../features/sessions/occurrences-slice";
import {
  cancelOccurrence,
  fetchSessionOccurrences,
} from "../../features/sessions/slice-extensions/occurrences-slice-extension";
import { ROUTES } from "../../routes";
import OccurrenceOverrideDialog from "../occurrences/OccurrenceOverrideDialog";

interface CancelOccurrenceActionProps {
  sessionId: string;
  /** Effective occurrence date (YYYY-MM-DD) shown on the attendance screen. */
  date: string;
}

/**
 * "Cancel this date" in the attendance screen's header. Opens the shared override
 * dialog (optional reason; warns that marked attendance will be removed), then
 * returns to the day list where the date shows its cancelled treatment.
 */
const CancelOccurrenceAction: React.FC<CancelOccurrenceActionProps> = ({
  sessionId,
  date,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The computed occurrence for this date — carries the original series date the
  // cancel endpoint expects (they differ when the date was rescheduled).
  const occurrences = useAppSelector(selectSessionOccurrences(sessionId));
  const occurrence = occurrences.find(
    (o) => o.effectiveDate === date && o.status !== OccurrenceStatus.Cancelled
  );

  useEffect(() => {
    dispatch(fetchSessionOccurrences({ bookingId: sessionId, from: date, to: date }));
  }, [dispatch, sessionId, date]);

  if (!occurrence) return null;

  const handleConfirm = async (payload: { reason?: string }) => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        cancelOccurrence({
          bookingId: sessionId,
          request: { occurrenceDate: occurrence.originalDate, reason: payload.reason },
        })
      ).unwrap();
      toast.success(t("sessions.occurrenceList.toastCancel"));
      navigate(`${ROUTES.ATTENDANCE}?date=${date}`);
    } catch (e: unknown) {
      setError(
        (e as { message?: string })?.message ?? t("sessions.occurrenceList.actionFailed")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        className="btn btn-secondary text-red-600 dark:text-red-400"
      >
        <Ban size={18} strokeWidth={2} />
        <span>{t("sessions.occurrenceList.cancel")}</span>
      </button>

      {open && (
        <OccurrenceOverrideDialog
          mode="cancel"
          occurrence={occurrence}
          busy={busy}
          error={error}
          onConfirm={handleConfirm}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default CancelOccurrenceAction;
